import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, FileSpreadsheet, XCircle } from 'lucide-react';
import { FileDropzone } from '../onboarding/FileDropzone';
import { ApiError } from '../../lib/api';
import {
  confirmReconciliationImport,
  previewReconciliationImport,
  type ReconImportPreview,
  type ReconImportResult,
  type ReconImportRow,
} from '../../lib/onboardingImportApi';
import { createExpenseCategoriesBulk } from '../../lib/expenseCategoriesApi';
import { EXPENSE_CATEGORIES_KEY } from '../../hooks/useExpenseCategories';
import { useCompanies } from '../../hooks/useCompanies';
import { useActiveCompanyId } from '../../store/useCompanyStore';
import { Select } from '../ui/Select';
import { ProjectMappingPanel, suggestedProjectMap } from '../imports/ProjectMappingPanel';

// Extrase → „Importă reconcilierea din Excel” (07_reconciliere_import.xlsx).
// Applied over the statements already uploaded (the firm's, or one): every
// file row is paired with a bank line (fingerprint, then the fallback key),
// filled in and saved as a DRAFT with the mark „din import”. Finalizing stays
// manual, statement by statement, in order.

const MAX_BYTES = 10 * 1024 * 1024;

type Step = 'upload' | 'preview' | 'result';
type Filter = 'all' | 'problems' | 'apply' | 'skip';

const MATCHED_BY: Record<NonNullable<ReconImportRow['matchedBy']>, string> = {
  fingerprint: 'amprentă',
  fallback: 'cheie de rezervă',
  amount: 'cont + dată + sumă',
  chosen: 'aleasă de tine',
};

const STATUS_LABEL: Record<ReconImportRow['data']['importStatus'], string> = {
  MANUAL_MATCHED: 'Reconciliată',
  IGNORED: 'Ignorată',
  UNMATCHED: 'De decis',
};

export function ReconciliationImportDialog({
  statements,
  onClose,
  onImported,
  companyId: fixedCompanyId,
}: {
  /** Statements the user may restrict the import to (current list page). */
  statements: { id: string; label: string }[];
  onClose: () => void;
  /** After a confirmed import (Setări → Onboarding records the batch). */
  onImported?: (result: ReconImportResult) => void;
  /** Import into this firm (Setări → Onboarding picks it); the picker starts on it. */
  companyId?: string | null;
}) {
  const qc = useQueryClient();
  const { data: companies } = useCompanies();
  const activeCompanyId = useActiveCompanyId();
  const [pickedCompany, setPickedCompany] = useState<string | null>(fixedCompanyId ?? null);
  const companyId =
    pickedCompany ??
    activeCompanyId ??
    companies?.find((c) => c.isDefault)?.id ??
    companies?.[0]?.id ??
    null;
  const [statementId, setStatementId] = useState('');

  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ReconImportPreview | null>(null);
  const [projectMap, setProjectMap] = useState<Record<string, string>>({});
  const [lineMap, setLineMap] = useState<Record<string, string>>({});
  const [result, setResult] = useState<ReconImportResult | null>(null);
  const [filter, setFilter] = useState<Filter>('problems');
  const [busy, setBusy] = useState<'reading' | 'importing' | 'categories' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const params = (pm: Record<string, string>, lm: Record<string, string>) => ({
    companyId,
    statementId: statementId || null,
    projectMap: pm,
    lineMap: lm,
  });

  async function runPreview(f: File, pm: Record<string, string>, lm: Record<string, string>) {
    setBusy('reading');
    setError(null);
    try {
      const first = await previewReconciliationImport(f, params(pm, lm));
      const suggested = Object.keys(pm).length === 0 ? suggestedProjectMap(first.projects) : {};
      if (Object.keys(suggested).length > 0) {
        setProjectMap(suggested);
        setPreview(await previewReconciliationImport(f, params(suggested, lm)));
      } else {
        setPreview(first);
      }
      setStep('preview');
    } catch (err) {
      setError(messageOf(err, 'Fișierul nu a putut fi citit.'));
    } finally {
      setBusy(null);
    }
  }

  function handleFile(f: File) {
    setError(null);
    if (!/\.xlsx$/i.test(f.name)) {
      setError('Este acceptat doar un fișier .xlsx.');
      return;
    }
    if (f.size > MAX_BYTES) {
      setError('Fișierul depășește limita de 10MB.');
      return;
    }
    setFile(f);
    setProjectMap({});
    setLineMap({});
    void runPreview(f, {}, {});
  }

  async function createMissingCategories() {
    if (!file || !preview) return;
    setBusy('categories');
    setError(null);
    try {
      await createExpenseCategoriesBulk(
        companyId,
        preview.unknownCategories.map((u) => ({
          code: u.code,
          name: u.code,
          kind: u.code.startsWith('venituri') || u.code.startsWith('income') ? 'INCOME' : 'EXPENSE',
        })),
      );
      void qc.invalidateQueries({ queryKey: EXPENSE_CATEGORIES_KEY });
      await runPreview(file, projectMap, lineMap);
    } catch (err) {
      setError(messageOf(err, 'Categoriile nu au putut fi create.'));
      setBusy(null);
    }
  }

  async function handleImport() {
    if (!file || !preview) return;
    setBusy('importing');
    setError(null);
    try {
      const res = await confirmReconciliationImport(file, {
        ...params(projectMap, lineMap),
        previewHash: preview.previewHash,
      });
      setResult(res);
      setStep('result');
      onImported?.(res);
      void qc.invalidateQueries({ queryKey: ['bank-statements'] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'PREVIEW_CHANGED') {
        setError(err.message);
        void runPreview(file, projectMap, lineMap);
      } else {
        setError(messageOf(err, 'Importul a eșuat.'));
      }
    } finally {
      setBusy(null);
    }
  }

  const rows = useMemo(() => {
    const all = preview?.rows ?? [];
    switch (filter) {
      case 'problems':
        return all.filter((r) => r.status !== 'valid');
      case 'apply':
        return all.filter((r) => r.action === 'apply' && r.status !== 'error');
      case 'skip':
        return all.filter((r) => r.action === 'skip' && r.status !== 'error');
      default:
        return all;
    }
  }, [preview, filter]);

  const rep = preview?.report;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-brand-600" />
            <h2 className="text-lg font-semibold text-gray-900">Importă reconcilierea din Excel</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy === 'importing'}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Închide"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {step === 'upload' && (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                {companies && companies.length > 0 && (
                  <Select
                    label="Firma"
                    value={companyId ?? ''}
                    onChange={(v) => setPickedCompany(v)}
                    options={companies.map((c) => ({
                      value: c.id,
                      label: c.displayName || c.legalName,
                    }))}
                  />
                )}
                <Select
                  label="Pe ce extrase"
                  value={statementId}
                  onChange={(v) => setStatementId(v)}
                  clearable
                  placeholder="Toate extrasele firmei"
                  options={statements.map((s) => ({ value: s.id, label: s.label }))}
                />
              </div>
              <p className="text-sm text-gray-600">
                Fișierul <span className="font-mono">07_reconciliere_import.xlsx</span> (format{' '}
                <span className="font-mono">firmio-onboarding-reconciliere</span>, versiunea 1),
                după ce extrasele sunt încărcate. Fiecare rând se potrivește cu linia lui din extras
                după amprentă; liniile se completează și se salvează ca ciornă, marcate „din
                import”. Finalizarea rămâne la tine, extras cu extras, în ordine. Liniile deja
                decise de mână și extrasele finalizate nu se schimbă.
              </p>
              <FileDropzone
                accept=".xlsx"
                hint="Un fișier .xlsx, până la 10MB"
                disabled={busy !== null}
                onFile={handleFile}
              />
              {busy === 'reading' && (
                <Spinner text="Se potrivesc rândurile cu liniile din extrase…" />
              )}
            </div>
          )}

          {step === 'preview' && preview && rep && (
            <>
              <p className="mb-3 text-xs text-gray-500">
                {file?.name} · {preview.format.format} v{preview.format.version}
                {preview.format.firm ? ` · ${preview.format.firm}` : ''}
                {preview.format.parser ? ` · parser ${preview.format.parser}` : ''}
              </p>
              <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Stat label="Pe amprentă" value={rep.byFingerprint} tone="green" />
                <Stat
                  label={`Pe cheia de rezervă${rep.byAmountOnly ? ` (${rep.byAmountOnly} fără descriere)` : ''}`}
                  value={rep.byFallback}
                  tone="blue"
                />
                <Stat label="Rânduri fără linie în Firmio" value={rep.withoutLine} tone="amber" />
                <Stat label="Linii din Firmio fără rând" value={rep.linesWithoutRow} tone="amber" />
                <Stat label="Se scriu" value={rep.apply} tone="green" />
                <Stat label="Deja la fel" value={rep.unchanged} tone="gray" />
                <Stat label="Lăsate cum sunt" value={rep.skipped} tone="gray" />
                <Stat label="Erori" value={rep.errors} tone="red" />
              </div>

              {preview.unknownCategories.length > 0 && (
                <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  <p className="font-medium">Categorii care nu există în firmă:</p>
                  <p className="mt-1 text-xs">
                    {preview.unknownCategories
                      .map((u) => `${u.code} (${u.rows.length})`)
                      .join(', ')}
                  </p>
                  <button
                    type="button"
                    onClick={() => void createMissingCategories()}
                    disabled={busy !== null}
                    className="mt-2 rounded-md bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                  >
                    Creează categoriile lipsă și verifică din nou
                  </button>
                </div>
              )}

              <ProjectMappingPanel
                projects={preview.projects}
                projectMap={projectMap}
                onChange={(next) => {
                  setProjectMap(next);
                  if (file) void runPreview(file, next, lineMap);
                }}
                disabled={busy !== null}
              />

              {rep.linesWithoutRow > 0 && (
                <details className="mb-3 text-xs text-gray-600">
                  <summary className="cursor-pointer">
                    {rep.linesWithoutRow} linii din Firmio nu au rând în fișier (rămân cum sunt)
                  </summary>
                  <ul className="mt-1 ml-4 list-disc">
                    {rep.linesWithoutRowSample.map((l) => (
                      <li key={l.lineId}>
                        {l.date} · {l.amount.toLocaleString('ro-RO')} · {l.description ?? '—'}
                      </li>
                    ))}
                  </ul>
                </details>
              )}

              <div className="mb-2 flex flex-wrap gap-1 text-xs">
                {(
                  [
                    [
                      'problems',
                      `Cu observații (${preview.rows.filter((r) => r.status !== 'valid').length})`,
                    ],
                    ['apply', `Se scriu (${rep.apply})`],
                    ['skip', `Lăsate cum sunt (${rep.skipped})`],
                    ['all', `Toate (${rep.fileRows})`],
                  ] as [Filter, string][]
                ).map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFilter(key)}
                    className={`rounded border px-2 py-1 ${
                      filter === key
                        ? 'border-brand-500 bg-brand-50 text-brand-700'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="max-h-[420px] overflow-auto rounded-md border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-xs">
                  <thead className="sticky top-0 z-10 bg-gray-50 text-left font-semibold uppercase tracking-wide text-gray-600">
                    <tr>
                      <th className="px-2 py-2">Rând</th>
                      <th className="px-2 py-2" />
                      <th className="px-2 py-2">Linia</th>
                      <th className="px-2 py-2">Decizie</th>
                      <th className="px-2 py-2">Potrivire</th>
                      <th className="px-2 py-2">Observații</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {rows.slice(0, 500).map((r) => (
                      <tr key={r.rowNumber} className={r.status === 'error' ? 'bg-red-500/10' : ''}>
                        <td className="px-2 py-1.5 align-top text-gray-500">{r.rowNumber}</td>
                        <td className="px-2 py-1.5 align-top">
                          <StatusIcon status={r.status} />
                        </td>
                        <td className="px-2 py-1.5 align-top">
                          <span className="text-gray-900">{r.data.counterparty || '—'}</span>
                          <span className="block text-gray-500">
                            {r.data.date} · {r.data.type === 'credit' ? '+' : '−'}
                            {r.data.amount?.toLocaleString('ro-RO', { minimumFractionDigits: 2 })}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 align-top text-gray-700">
                          {STATUS_LABEL[r.data.importStatus]}
                          {r.data.categoryCode && (
                            <span className="block font-mono text-gray-500">
                              {r.data.categoryCode}
                            </span>
                          )}
                          {r.data.allocations.length > 0 && (
                            <span className="block text-gray-500">
                              {r.data.allocations.map((a) => a.invoiceNumber).join(', ')}
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 align-top text-gray-600">
                          {r.matchedBy ? MATCHED_BY[r.matchedBy] : '—'}
                          {r.candidates && (
                            <select
                              aria-label={`Linia pentru rândul ${r.rowNumber}`}
                              value={lineMap[String(r.rowNumber)] ?? ''}
                              disabled={busy !== null}
                              onChange={(e) => {
                                const next = { ...lineMap };
                                if (e.target.value) next[String(r.rowNumber)] = e.target.value;
                                else delete next[String(r.rowNumber)];
                                setLineMap(next);
                                if (file) void runPreview(file, projectMap, next);
                              }}
                              className="mt-1 block w-56 rounded border border-gray-300 px-1 py-0.5 text-xs"
                            >
                              <option value="">— alege linia —</option>
                              {r.candidates.map((c) => (
                                <option key={c.lineId} value={c.lineId}>
                                  {c.statement} · {c.description ?? '—'}
                                </option>
                              ))}
                            </select>
                          )}
                        </td>
                        <td className="px-2 py-1.5 align-top">
                          {r.issues.map((issue, i) => (
                            <span
                              key={i}
                              className={`block ${r.status === 'error' ? 'text-red-700' : 'text-amber-700'}`}
                            >
                              {issue}
                            </span>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rows.length > 500 && (
                  <p className="px-3 py-2 text-xs text-gray-500">
                    Se văd primele 500 din {rows.length}; restul sunt la fel de incluse în import.
                  </p>
                )}
              </div>
            </>
          )}

          {step === 'result' && result && (
            <div className="space-y-3">
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-4">
                <p className="flex items-center gap-2 text-sm font-semibold text-emerald-700">
                  <CheckCircle2 className="h-4 w-4" />
                  Reconcilierea a fost importată ca ciornă
                </p>
                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Stat label="Linii scrise" value={result.applied} tone="green" />
                  <Stat label="Extrase atinse" value={result.statementsTouched} tone="blue" />
                  <Stat label="Lăsate cum sunt" value={result.skipped} tone="gray" />
                  <Stat label="Erori (neimportate)" value={result.errors} tone="red" />
                </div>
              </div>
              <p className="text-sm text-gray-600">
                Următorul pas: deschide extrasele în ordine cronologică, cont cu cont, și
                finalizează fiecare. Liniile scrise acum sunt marcate „din import”.{' '}
                <Link
                  to={`/financials/bank-statements?importBatchId=${result.batchId}`}
                  onClick={onClose}
                  className="font-medium text-brand-700 hover:underline"
                >
                  Vezi extrasele din acest import
                </Link>
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
          {step === 'preview' && (
            <>
              {busy === 'reading' && <Spinner text="Se recalculează…" />}
              <button
                type="button"
                onClick={() => {
                  setStep('upload');
                  setPreview(null);
                  setFile(null);
                }}
                disabled={busy !== null}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Alege alt fișier
              </button>
              <button
                type="button"
                onClick={() => void handleImport()}
                disabled={busy !== null || (rep?.apply ?? 0) === 0}
                className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {busy === 'importing' ? 'Se importă…' : `Scrie ${rep?.apply ?? 0} linii (ciornă)`}
              </button>
            </>
          )}
          {step !== 'preview' && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {step === 'result' ? 'Gata' : 'Anulează'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Spinner({ text }: { text: string }) {
  return (
    <p className="flex items-center gap-2 text-sm text-gray-500">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
      {text}
    </p>
  );
}

function StatusIcon({ status }: { status: ReconImportRow['status'] }) {
  if (status === 'valid')
    return <CheckCircle2 className="h-4 w-4 text-green-600" aria-label="Valid" />;
  if (status === 'warning')
    return <AlertTriangle className="h-4 w-4 text-amber-500" aria-label="Avertisment" />;
  return <XCircle className="h-4 w-4 text-red-600" aria-label="Eroare" />;
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'green' | 'blue' | 'amber' | 'red' | 'gray';
}) {
  const toneClass = {
    green: 'text-green-700',
    blue: 'text-blue-700',
    amber: 'text-amber-700',
    red: 'text-red-700',
    gray: 'text-gray-700',
  }[tone];
  return (
    <div className="rounded border border-gray-200 bg-white px-3 py-2 text-center">
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`text-lg font-semibold ${toneClass}`}>{value}</p>
    </div>
  );
}

function messageOf(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}
